// @flow
// $FlowFixMe
import { autoRehydrate } from 'redux-persist'
import { connectRouter, routerMiddleware } from 'connected-react-router'
import { createBrowserHistory } from 'history'
import { createLogger } from 'redux-logger'
import { createStore, applyMiddleware, combineReducers, compose } from 'redux'
import { errorReporter } from './customMiddleware/errorReporter'
import { LOGOUT } from '../ducks/user'
import device from '../ducks/device'
import location from '../ducks/location'
import LogRocket from 'logrocket'
import schedule from '../ducks/schedule'
import thunkMiddleware from 'redux-thunk'
import track from '../util/tracker'
import user from '../ducks/user/reducer'
import zone from '../ducks/zone'

import type { Store as LocationState } from '../ducks/location'
import type { Store as UserState } from '../ducks/user/reducer'
import type { ScheduleModel } from '../ducks/schedule/types'
import type { DeviceModel } from '../ducks/device/types'
import type { ZoneModel } from '../ducks/zone/types'

// Types //////////////////////////////////////////////////////////////////////

export type State = {
  +device: {
    +META: {},
    +[deviceId: string]: DeviceModel
  },
  +user: UserState,
  +location: LocationState,
  +zone: {
    +META: {},
    +[zoneId: string]: ZoneModel
  },
  +schedule: {
    +META: {},
    +[scheduleId: string]: ScheduleModel
  },
  +router: {
    location: {
      pathname: string,
      search: string,
      hash?: string,
      key?: string
    }
  }
}

// Analytics Middleware ///////////////////////////////////////////////////////

/* eslint-disable no-unused-vars */

/**
 * Tracking/Analytics Middleware
 * @param  {object} store - Redux store
 * @return {object} - Result of calling `next()`
 */
const analyticsMiddleware = store => next => action => {
  if (action.type && action.type === '@@router/LOCATION_CHANGE') {
    try {
      const pathname = action.payload.pathname
        ? action.payload.pathname
        : action.payload.location.pathname
      const search = action.payload.search
        ? action.payload.search
        : action.payload.location.search

      track.pageview(pathname + search)
      // eslint-disable-next-line no-empty
    } catch (err) {}
  }

  return next(action)
}

/* eslint-enable no-unused-vars */

export const history = createBrowserHistory()

// Logging middleware /////////////////////////////////////////////////////////

const loggerMiddleware = createLogger({
  // collapse all non-error action logs
  collapsed: (getState, action) => !action.error
})

// Combine reducers ///////////////////////////////////////////////////////////

/**
 * Combine all reducers into a single root reducer
 * @type {func} aggregated single reducer
 */
const appReducer = combineReducers({
  device: device,
  user: user,
  location: location,
  zone: zone,
  schedule: schedule,
  router: connectRouter(history)
})

/**
 * prune app state down to only routing reducer before it gets to the combined
 * app reducer
 * @param {object} state  previous app state
 * @param {object} action the action that was just fired
 * @return {object} if there were changes: a new app state, otherwise the same state
 */
const rootReducer = (state, action) => {
  if (action.type === LOGOUT) {
    const { router } = state
    /* eslint-disable no-param-reassign */
    state = { router }
    /* eslint-enable no-param-reassign */
  } else if (action.type === 'RACHIO/USER/MASK_AS_USER') {
    const { router } = state
    /* eslint-disable no-param-reassign */
    state = {
      router,
      user: {
        // $FlowFixMe
        META: { ...state.user.META },
        isMaskingAsUser: true
      }
    }
    /* eslint-enable no-param-reassign */
  }
  return appReducer(state, action)
}

// Create and export actual store /////////////////////////////////////////////

export const buildStore = () =>
  /* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
   * v0.98.0. To view the error, delete this comment and run Flow. */
  createStore(
    rootReducer,
    compose(
      applyMiddleware(
        routerMiddleware(history), // router middleware
        thunkMiddleware, // lets us dispatch() promises
        analyticsMiddleware, // actions as analytics
        loggerMiddleware, // log actions
        LogRocket.reduxMiddleware(), // LogRocket
        errorReporter // log errors
      ),
      window.__REDUX_DEVTOOLS_EXTENSION__
        ? window.__REDUX_DEVTOOLS_EXTENSION__({
            maxAge: 100
          })
        : f => f,
      autoRehydrate()
    )
  )

export const Store = buildStore()
